import React, { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { importAppleHealth } from '../services/api';
import toast from 'react-hot-toast';

export default function ImportPage() {
  const { currentUser } = useAuth();
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [dragOver, setDragOver] = useState(false);
  const [since, setSince] = useState('');
  const [until, setUntil] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFile = async (file: File) => {
    if (!file.name.endsWith('.xml') && !file.name.endsWith('.zip')) {
      toast.error('Please select an Apple Health export .xml file');
      return;
    }
    if (!currentUser) return;

    setImporting(true);
    setResult(null);
    try {
      const token = await currentUser.getIdToken();
      const res = await importAppleHealth(token, file, since || undefined, until || undefined);
      setResult(res);
      toast.success(`Imported ${res.days_imported || 0} days of health data!`);
    } catch (e: any) {
      toast.error(e.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Import Apple Health Data</h1>
        <p className="text-gray-500 text-sm mt-1">
          Bulk import your health history from the iPhone Health app export.
        </p>
      </div>

      {/* How-to guide */}
      <div className="bg-blue-50 rounded-2xl p-5">
        <h2 className="font-semibold text-blue-800 mb-3">How to export from Apple Health</h2>
        <ol className="text-sm text-blue-700 space-y-2 list-decimal list-inside">
          <li>Open the <strong>Health</strong> app on your iPhone</li>
          <li>Tap your profile photo (top right)</li>
          <li>Scroll down and tap <strong>"Export All Health Data"</strong></li>
          <li>Choose <strong>AirDrop, Files, or email</strong> to transfer to your Mac</li>
          <li>Unzip the file — you'll see <strong>export.xml</strong> inside</li>
          <li>Upload <strong>export.xml</strong> below</li>
        </ol>
      </div>

      {/* Date range filter */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-3">
        <h2 className="font-semibold text-gray-800">Date Range (optional)</h2>
        <p className="text-xs text-gray-500">
          Leave blank to import all data. Set dates to import only a specific period. Re-importing the same dates is safe — existing data gets updated, not duplicated.
        </p>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">From</label>
            <input
              type="date"
              value={since}
              onChange={(e) => setSince(e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">To</label>
            <input
              type="date"
              value={until}
              onChange={(e) => setUntil(e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
        </div>
      </div>

      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => fileRef.current?.click()}
        className={`border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-colors ${
          dragOver
            ? 'border-emerald-400 bg-emerald-50'
            : 'border-gray-300 hover:border-emerald-400 hover:bg-emerald-50/30'
        }`}
      >
        <input ref={fileRef} type="file" accept=".xml,.zip" className="hidden" onChange={onFileChange} />
        <div className="text-4xl mb-3">{importing ? '...' : '+'}</div>
        <p className="font-medium text-gray-700">
          {importing ? 'Importing your health data...' : 'Drop export.xml here or click to browse'}
        </p>
        <p className="text-sm text-gray-400 mt-1">Supports Apple Health export.xml</p>
        {(since || until) && (
          <p className="text-xs text-emerald-600 mt-2">
            Filtering: {since || 'start'} to {until || 'now'}
          </p>
        )}
      </div>

      {/* Result */}
      {result && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5">
          <h2 className="font-semibold text-emerald-800 mb-3">Import Complete</h2>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="bg-white rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-emerald-600">{result.days_imported || 0}</div>
              <div className="text-gray-500 text-xs">Days imported</div>
            </div>
            <div className="bg-white rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-gray-700">{result.records_saved || result.days_imported || 0}</div>
              <div className="text-gray-500 text-xs">Records saved</div>
            </div>
          </div>
        </div>
      )}

      {/* Privacy note */}
      <div className="bg-gray-50 rounded-xl px-4 py-3 flex items-start gap-3">
        <p className="text-xs text-gray-500">
          Your health data is stored privately in your personal Firestore document and never shared with other users.
          Re-importing the same dates updates existing records — no duplicates are created.
        </p>
      </div>
    </div>
  );
}
